import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Trash2, Plus, Save, ArrowLeft } from "lucide-react";

const GST_RATES = [0, 5, 12, 18, 28];

export default function InvoiceEditor({ kind = "invoice" }) {
  const { id } = useParams();
  const nav = useNavigate();
  const isQuote = kind === "quotation";
  const isNew = id === "new";
  const endpoint = isQuote ? "/quotations" : "/invoices";
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    client_lead_id: "", client_name: "", client_mobile: "", client_email: "", client_address: "", client_gstin: "", client_state: "",
    invoice_date: new Date().toISOString().slice(0, 10), due_date: "",
    place_of_supply: "", interstate: false,
    items: [{ description: "", service: "", hsn_sac: "", quantity: 1, rate: 0, gst_rate: 18 }],
    discount_type: "amount", discount_value: 0, round_off: true,
    notes: "", terms: "1. Payment due within 15 days.\n2. Late payments subject to 1.5% interest.",
    payment_status: "Unpaid", payment_mode: "", amount_paid: 0, connector_id: "",
  });
  const [computed, setComputed] = useState({ subtotal: 0, discount: 0, taxable_amount: 0, cgst: 0, sgst: 0, igst: 0, round_off: 0, grand_total: 0, amount_in_words: "" });
  const [saving, setSaving] = useState(false);
  const [connectors, setConnectors] = useState([]);

  useEffect(() => {
    api.get("/meta").then((r) => setServices(r.data.service_types));
    api.get("/users").then((r) => setConnectors(r.data)).catch(() => {});
    if (!isNew) {
      api.get(`${endpoint}/${id}`).then((r) => { setForm(r.data); setComputed(r.data); });
    }
    // eslint-disable-next-line
  }, [id]);

  // live compute on any change
  useEffect(() => {
    const items = form.items.map((it) => {
      const amount = (Number(it.quantity) || 0) * (Number(it.rate) || 0);
      const tax = amount * (Number(it.gst_rate) || 0) / 100;
      const cgst = form.interstate ? 0 : tax / 2;
      const sgst = form.interstate ? 0 : tax - cgst;
      const igst = form.interstate ? tax : 0;
      return { ...it, amount, tax, cgst, sgst, igst, total: amount + tax };
    });
    const subtotal = items.reduce((s, i) => s + i.amount, 0);
    const totalTax = items.reduce((s, i) => s + i.tax, 0);
    const dv = Number(form.discount_value) || 0;
    const discount = form.discount_type === "percent" ? subtotal * dv / 100 : dv;
    const taxable = subtotal - discount;
    const ratio = subtotal > 0 ? taxable / subtotal : 1;
    const cgst = items.reduce((s, i) => s + i.cgst, 0) * ratio;
    const sgst = items.reduce((s, i) => s + i.sgst, 0) * ratio;
    const igst = items.reduce((s, i) => s + i.igst, 0) * ratio;
    let grand = taxable + totalTax * ratio;
    let roundOff = 0;
    if (form.round_off) { const g = Math.round(grand); roundOff = g - grand; grand = g; }
    setComputed({ subtotal, discount, taxable_amount: taxable, cgst, sgst, igst, total_tax: totalTax * ratio, round_off: roundOff, grand_total: grand });
  }, [form]);

  const setItem = (idx, key, val) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: val } : it) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", service: "", hsn_sac: "", quantity: 1, rate: 0, gst_rate: 18 }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post(endpoint, form);
        nav(`${isQuote ? "/quotations" : "/invoices"}/${r.data.id}`);
      } else {
        await api.patch(`${endpoint}/${id}`, form);
        alert("Saved");
      }
    } catch (e) { alert(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-6xl" data-testid={`${kind}-editor`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={() => nav(-1)} className="text-sm link-blue inline-flex items-center gap-1"><ArrowLeft size={14} /> Back</button>
        <h1 className="font-heading text-3xl font-extrabold">{isNew ? `New ${isQuote ? "Quotation" : "Invoice"}` : form.number}</h1>
        <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="save-invoice"><Save size={16} /> {saving ? "Saving…" : "Save"}</button>
      </div>

      <div className="card p-5 grid md:grid-cols-2 gap-4">
        <div>
          <h3 className="font-heading font-bold mb-2">Customer</h3>
          <div className="space-y-2">
            <input className="input" placeholder="Client Name *" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="inv-client-name" />
            <div className="grid grid-cols-2 gap-2">
              <input className="input" placeholder="Mobile" value={form.client_mobile} onChange={(e) => setForm({ ...form, client_mobile: e.target.value })} data-testid="inv-client-mobile" />
              <input className="input" placeholder="Email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
            </div>
            <input className="input" placeholder="Address" value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <input className="input uppercase" placeholder="GSTIN" value={form.client_gstin} onChange={(e) => setForm({ ...form, client_gstin: e.target.value.toUpperCase() })} />
              <input className="input" placeholder="State" value={form.client_state} onChange={(e) => setForm({ ...form, client_state: e.target.value })} />
            </div>
          </div>
        </div>
        <div>
          <h3 className="font-heading font-bold mb-2">{isQuote ? "Quotation" : "Invoice"} Details</h3>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-muted-foreground">Date *</label>
              <input type="date" className="input mt-1" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="inv-date" /></div>
            <div><label className="text-xs text-muted-foreground">{isQuote ? "Valid Till" : "Due Date"}</label>
              <input type="date" className="input mt-1" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          </div>
          <div className="mt-2"><label className="text-xs text-muted-foreground">Place of Supply</label>
            <input className="input mt-1" value={form.place_of_supply} onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })} placeholder="e.g. Maharashtra" /></div>
          <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.interstate} onChange={(e) => setForm({ ...form, interstate: e.target.checked })} data-testid="inv-interstate" /> Inter-state (apply IGST instead of CGST/SGST)
          </label>
          <div className="mt-2">
            <label className="text-xs text-muted-foreground">Linked Connector</label>
            <select className="input mt-1" value={form.connector_id} onChange={(e) => setForm({ ...form, connector_id: e.target.value })}>
              <option value="">— None —</option>
              {connectors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <h3 className="font-heading font-bold">Items</h3>
          <button className="btn btn-outline !py-1 !text-xs" onClick={addItem} data-testid="inv-add-item"><Plus size={12} /> Add Line</button>
        </div>
        <table className="table-clean">
          <thead><tr><th>Description</th><th>Service</th><th>HSN/SAC</th><th>Qty</th><th>Rate</th><th>GST%</th><th>Amount</th><th>Tax</th><th>Total</th><th></th></tr></thead>
          <tbody>
            {form.items.map((it, idx) => (
              <tr key={idx}>
                <td><input className="input !py-1" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} data-testid={`inv-item-desc-${idx}`} /></td>
                <td>
                  <select className="input !py-1 !w-[130px]" value={it.service} onChange={(e) => setItem(idx, "service", e.target.value)}>
                    <option value="">—</option>{services.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td><input className="input !py-1 !w-[80px]" value={it.hsn_sac} onChange={(e) => setItem(idx, "hsn_sac", e.target.value)} /></td>
                <td><input type="number" step="0.01" min="0" className="input !py-1 !w-[60px] text-right" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} /></td>
                <td><input type="number" step="0.01" min="0" className="input !py-1 !w-[90px] text-right" value={it.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} data-testid={`inv-item-rate-${idx}`} /></td>
                <td>
                  <select className="input !py-1 !w-[60px]" value={it.gst_rate} onChange={(e) => setItem(idx, "gst_rate", Number(e.target.value))}>
                    {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </td>
                <td className="tabular text-right">₹{((it.quantity || 0) * (it.rate || 0)).toFixed(2)}</td>
                <td className="tabular text-right">₹{(((it.quantity || 0) * (it.rate || 0)) * (it.gst_rate || 0) / 100).toFixed(2)}</td>
                <td className="tabular text-right font-semibold">₹{(((it.quantity || 0) * (it.rate || 0)) * (1 + (it.gst_rate || 0) / 100)).toFixed(2)}</td>
                <td><button className="btn btn-ghost !p-1 text-red-600" onClick={() => removeItem(idx)}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-5 space-y-3">
          <h3 className="font-heading font-bold">Discount & Notes</h3>
          <div className="flex gap-2 items-center">
            <select className="input !w-auto" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}>
              <option value="amount">₹ Amount</option><option value="percent">% Percent</option>
            </select>
            <input type="number" step="0.01" min="0" className="input" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="inv-discount" />
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.round_off} onChange={(e) => setForm({ ...form, round_off: e.target.checked })} /> Round off grand total</label>
          <div><label className="text-xs text-muted-foreground">Notes</label>
            <textarea className="input mt-1 min-h-[70px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground">Terms & Conditions</label>
            <textarea className="input mt-1 min-h-[90px]" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></div>
          {!isQuote && (
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
              <div><label className="text-xs text-muted-foreground">Payment Status</label>
                <select className="input mt-1" value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} data-testid="inv-payment-status">
                  {["Unpaid", "Partially Paid", "Paid"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select></div>
              <div><label className="text-xs text-muted-foreground">Payment Mode</label>
                <select className="input mt-1" value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}>
                  <option value="">—</option>{["Cash", "UPI", "Bank Transfer", "Card"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select></div>
            </div>
          )}
        </div>

        <div className="card p-5">
          <h3 className="font-heading font-bold mb-3">Summary</h3>
          <div className="space-y-1 text-sm">
            <Row label="Subtotal" value={computed.subtotal} />
            <Row label="Discount" value={-computed.discount} />
            <Row label="Taxable" value={computed.taxable_amount} />
            {form.interstate ? <Row label="IGST" value={computed.igst} /> : <><Row label="CGST" value={computed.cgst} /><Row label="SGST" value={computed.sgst} /></>}
            {form.round_off && <Row label="Round Off" value={computed.round_off} />}
            <div className="border-t border-border pt-2 mt-2 flex justify-between items-baseline">
              <span className="font-heading font-bold">Grand Total</span>
              <span className="font-heading text-2xl font-extrabold tabular" data-testid="inv-grand-total">₹{computed.grand_total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className="tabular">₹{(value || 0).toFixed(2)}</span></div>;
}
